import React, { useEffect, useState } from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { SettingsState } from '../../organisms/SelectSettings';
import { Paper, Table, TableContainer, TableBody, TableRow, TableCell, TextField, TableHead } from '@material-ui/core';
import {
  GroupListType,
  SIG_CMP_GROUP_SET,
  DeviceSelectItem,
  GetRawWaveformsComparisonGroupList,
  PutRawWaveformsComparisonGroupList,
} from '../../../function/GroupDataModel';
import ItemComboBox, { ComboBoxItemType } from '../../atoms/ComboBox/ItemComboBox';
import DeviceMeasItem from './DeviceMeasItem';
import SaveCancel from './SaveCancel';
import { useModal } from '../../../function/useModal';
import ConfirmDialog, { ConfirmDialogResponse } from '../../atoms/Dialog/ConfirmDialog';
import MessageDialog, { MessageDialogResponse } from '../../atoms/Dialog/MessageDialog';
import { useTranslation } from 'react-i18next';
import CreateGroupDialog, { CreateGroupDialogResponse } from './CreateGroupDialog';

type RawWaveformsComparisonSettingProps = {
  editData: SettingsState;
};

type SaveParameter = {
  list: SIG_CMP_GROUP_SET[];
};

const MaxGroup: number = 256;

const useStyles = makeStyles(
  createStyles({
    tableStyle: {
      width: '400px',
      borderStyle: 'none',
    },
    tableHeader: {
      borderWidth: '1px',
      borderColor: '#dcdcdc',
      borderStyle: 'solid',
      backgroundColor: 'grey',
      textAlign: 'center',
      color: 'white',
      padding: '1px',
      minWidth: '80px',
      borderCollapse: `collapse`,
    },
    rowStyle: {
      borderStyle: 'none',
    },
    cellStyle: {
      borderStyle: 'none',
    },
    cellName: {
      width: '140px',
    },
    cellData: {
      width: '220px',
    },
    selectNo: {
      width: '120px',
    },
    selectName: {
      width: '200px',
    },
    dropdownStyle: {
      maxHeight: 300,
    },
    gridStyle: {
      borderStyle: 'solid',
      maxWidth: '500px',
    },
  })
);

const RawWaveformsComparisonSetting: React.FC<RawWaveformsComparisonSettingProps> = (
  props: RawWaveformsComparisonSettingProps
) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [groupNo, setGroupNo] = useState<number>(0);
  const [editList, setEditList] = useState<SIG_CMP_GROUP_SET[]>([]);
  const [groupName, setGroupName] = useState<string>('');
  const [selectList, setSelectList] = useState<DeviceSelectItem[]>([]);
  const [groupCombos, setGroupCombos] = useState<ComboBoxItemType[]>();
  const [plcNo1, setPlcNo1] = useState<number>(0);
  const [setNo1, setSetNo1] = useState<number>(0);
  const [plcNo2, setPlcNo2] = useState<number>(0);
  const [setNo2, setSetNo2] = useState<number>(0);
  const [flgCfmDlg, actCfmDlg] = useModal<ConfirmDialogResponse>();
  const [flgMsgDlg, actMsgDlg] = useModal<MessageDialogResponse>();
  const [dlgmsg, setDlgmsg] = useState<string>('');
  const [flgCrtDlg, actCrtDlg] = useModal<CreateGroupDialogResponse>();

  const cfmdlg = flgCfmDlg ? (
    <ConfirmDialog
      dialog={actCfmDlg}
      message={dlgmsg}
      action={t('RawWaveformsComparisonSetting.はい', 'はい')}
      cancel={t('RawWaveformsComparisonSetting.いいえ', 'いいえ')}
    />
  ) : null;
  const msgdlg = flgMsgDlg ? <MessageDialog dialog={actMsgDlg} message={dlgmsg} /> : null;
  const crtdlg = flgCrtDlg ? (
    <CreateGroupDialog dialog={actCrtDlg} exists={groupCombos?.map<number>((gc) => gc.index)} maxGroup={MaxGroup} />
  ) : null;

  console.log('-- RawWaveformsComparisonSetting --');

  useEffect(() => {
    console.log('-- fetch raw waveform settings --');
    GetRawWaveformsComparisonGroupList().then((result: GroupListType) => {
      const groupList: SIG_CMP_GROUP_SET[] = result.SIG_CMP_GROUP_SETs.filter((ph) => ph.GROUP_NAME !== '');
      setEditList(groupList);
      
      console.debug(["v-----setSelectList----",result]);
      //生波形で必要ない項目は省く
      result.DeviceSelects.forEach((r) => (r.MeasureSelects = r.MeasureSelects.filter((rr) => rr.FFT_INVALID === 0)));
      
      setSelectList(result.DeviceSelects);
      console.debug(["setSelectList----",result]);

      if (groupList.length > 0) {
        const grp = groupList[0];
        setGroupNo(grp.GROUP_NO);
        setGroupName(grp.GROUP_NAME);

        setPlcNo1(grp.PLC_NO1);
        setSetNo1(grp.SET_NO1);
        setPlcNo2(grp.PLC_NO2);
        setSetNo2(grp.SET_NO2);
      }

      props.editData.SaveFunction = saveCurrentSetting;
      props.editData.IsUpdate = false;
    });
  }, [props.editData, props.editData.CancelCondition]);

  useEffect(() => {
    const groups = editList?.map<ComboBoxItemType>((grp) => {
      return { index: grp.GROUP_NO, name: grp.GROUP_NO.toString() };
    });
    setGroupCombos(groups);

    if (!editList.find((el) => el.GROUP_NO == groupNo)) {
      if (editList.length > 0) {
        const grp = editList[0];
        setGroupNo(grp.GROUP_NO);
      }
    }
  }, [editList]);

  useEffect(() => {
    const grp = editList.find((ed) => ed.GROUP_NO === groupNo);
    if (grp) {
      setGroupName(grp.GROUP_NAME ?? '');

      if (selectList) {
        setPlcNo1(grp.PLC_NO1);
        setSetNo1(grp.SET_NO1);
        setPlcNo2(grp.PLC_NO2);
        setSetNo2(grp.SET_NO2);
      }
    }
  }, [groupNo]);

  const groupChange = (no: number) => {
    updateDispFlg();
    setGroupNo(no);
  };
  const updateDispFlg = () => {
    const grp = editList?.find((ed) => ed.GROUP_NO === groupNo);
    if (grp) {
      if (grp.GROUP_NAME === '' && grp.PLC_NO1 === 0 && grp.PLC_NO2 === 0) {
        grp.DSP_FLG = 0;
      } else {
        grp.DSP_FLG = 1;
      }
    }
  };
  const groupNameChange = (name: string) => {
    const grp = editList?.find((ed) => ed.GROUP_NO === groupNo);
    if (grp) {
      setGroupName(name);
      grp.GROUP_NAME = name;

      props.editData.Target = { list: editList };
      props.editData.IsUpdate = true;
    }
  };
  const groupPlcSet1 = (plc: number, set: number, kind: number) => {
    const grp = editList?.find((ed) => ed.GROUP_NO === groupNo);
    if (grp) {
      grp.PLC_NO1 = plc;
      grp.SET_NO1 = set;

      props.editData.Target = { list: editList };
      props.editData.IsUpdate = true;

      updateDispFlg();
      setPlcNo1(plc);
      setSetNo1(set);
    }
  };
  const groupPlcSet2 = (plc: number, set: number, kind: number) => {
    const grp = editList?.find((ed) => ed.GROUP_NO === groupNo);
    if (grp) {
      grp.PLC_NO2 = plc;
      grp.SET_NO2 = set;

      props.editData.Target = { list: editList };
      props.editData.IsUpdate = true;

      updateDispFlg();
      setPlcNo2(plc);
      setSetNo2(set);
    }
  };
  const saveCurrentSetting = (param: object): Promise<string> => {
    return new Promise((resolve, reject) => {
      const sp = param as SaveParameter;
      if (sp && sp.list) {
        const errmsg = validateSetting(sp.list);
        if (errmsg === '') {
          PutRawWaveformsComparisonGroupList(sp.list)
            .then((count: number) => {
              console.log(count);

              resolve('done');
            })
            .catch((reasone) => reject(reasone));
        } else {
          setDlgmsg(errmsg);
          actMsgDlg.open().apply((_) => setDlgmsg(''));
          reject('not save');
        }
      } else {
        reject('data nothing');
      }
    });
  };
  const validateSetting = (groups: SIG_CMP_GROUP_SET[]): string => {
    for (var idx = 0; idx < groups.length; idx++) {
      if (groups[idx].GROUP_NAME == '') {
        return t('RawWaveformsComparisonSetting.グループ名必須', 'グループ名は必須です');
      }
    }
    return '';
  };
  const deleteCurrentSetting = (param: object): Promise<string> => {
    return new Promise((resolve, reject) => {
      const sp = param as SaveParameter;
      if (sp && sp.list) {
        PutRawWaveformsComparisonGroupList(sp.list)
          .then((count: number) => {
            console.log(count);

            resolve('done');
          })
          .catch((reasone) => reject(reasone));
      } else {
        reject('data nothing');
      }
    });
  };

  return (
    <div>
      <TableContainer>
        <Table className={classes.tableStyle}>
          <TableBody>
            <TableRow className={classes.rowStyle}>
              <TableCell className={classes.cellName + ' ' + classes.cellStyle}>
                {t('RawWaveformsComparisonSetting.グループNo', 'グループNo')}
              </TableCell>
              <TableCell className={classes.cellData + ' ' + classes.cellStyle}>
                <ItemComboBox
                  itemList={groupCombos}
                  label=""
                  value={groupNo}
                  onChangeEvent={(e) => {
                    if (typeof e.target.value === 'number') {
                      groupChange(e.target.value);
                    } else if (typeof e.target.value === 'string') {
                      groupChange(parseInt(e.target.value));
                    }
                  }}
                />
              </TableCell>
            </TableRow>
            <TableRow className={classes.rowStyle}>
              <TableCell className={classes.cellName + ' ' + classes.cellStyle}>
                {t('RawWaveformsComparisonSetting.グループ名称', 'グループ名称')}
              </TableCell>
              <TableCell className={classes.cellData + ' ' + classes.cellStyle}>
                <TextField
                  value={groupName}
                  inputProps={{maxLength:32}}
                  onChange={(e) => {
                    groupNameChange(e.target.value);
                  }}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Table className={classes.gridStyle}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader} width={'180px'}>
                {t('RawWaveformsComparisonSetting.機器番号', '機器番号')}
              </TableCell>
              <TableCell className={classes.tableHeader} width={'200px'}>
                {t('RawWaveformsComparisonSetting.計測種別', '計測種別')}
              </TableCell>
              <TableCell className={classes.tableHeader} width={'200px'}>
                {t('RawWaveformsComparisonSetting.項目', '項目')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <DeviceMeasItem selectList={selectList} plcNo={plcNo1} setNo={setNo1} kind={0} plcSetFunc={groupPlcSet1} />
            <DeviceMeasItem selectList={selectList} plcNo={plcNo2} setNo={setNo2} kind={0} plcSetFunc={groupPlcSet2} />
          </TableBody>
        </Table>
        <SaveCancel
          saveAction={() => {
            setDlgmsg(t('RawWaveformsComparisonSetting.保存しますか?', '保存しますか?'));
            actCfmDlg
              .open()
              .apply((_) => {
                saveCurrentSetting({ list: editList }).then((_) => {
                  props.editData.IsUpdate = false;
                  // props.editData.CloseSetting();
                });
              })
              .cancel(() => {});
          }}
          cancelAction={() => {
            if (props.editData.IsUpdate) {
              setDlgmsg(
                t(
                  'RawWaveformsComparisonSetting.保存していないデータを破棄します。\nよろしいですか?',
                  '保存していないデータを破棄します。\nよろしいですか?'
                )
              );
              actCfmDlg
                .open()
                .apply((_) => {
                  // props.editData.IsUpdate = false;
                  props.editData.UpdateCancel();
                  // props.editData.CloseSetting();
                })
                .cancel(() => {});
            } else {
              // props.editData.CloseSetting();
            }
          }}
          createAction={() => {
            actCrtDlg.open().apply((info) => {
              const grp = editList.find((ed) => ed.GROUP_NO == info.no);
              if (!grp) {
                const newGrp: SIG_CMP_GROUP_SET = {
                  GROUP_NO: info.no,
                  GROUP_NAME: '',
                  DSP_FLG: 0,
                  PLC_NO1: 0,
                  SET_NO1: 0,
                  PLC_NO2: 0,
                  SET_NO2: 0,
                  Time_No1: '',
                  Time_No2: '',
                };
                const newList = editList.concat(newGrp).sort((g1, g2) => g1.GROUP_NO - g2.GROUP_NO);
                setEditList(newList);
              }
              setGroupNo(info.no);
            });
          }}
          deleteAction={() => {
            setDlgmsg(t('RawWaveformsComparisonSetting.削除しますか?', '削除しますか?'));
            actCfmDlg.open().apply((_) => {
              const grp = editList.find((ed) => ed.GROUP_NO == groupNo);
              if (grp) {
                grp.GROUP_NAME = '';
                deleteCurrentSetting({ list: editList }).then((_) => {
                  props.editData.IsSaveClose = true;
                  props.editData.IsUpdate = false;
                  setEditList(editList.filter((ph) => ph.GROUP_NAME !== ''));
                });
              }
            });
          }}
        />
      </TableContainer>
      {cfmdlg}
      {msgdlg}
      {crtdlg}
    </div>
  );
};

export default RawWaveformsComparisonSetting;
